/**
 * Page Selector Component
 * ========================
 * This component is called from: PageBuilder/index.js
 * 
 * Purpose:
 * Provides a dropdown to select and load existing pages for editing.
 * 
 * Data Flow:
 * PageSelector → firestoreService → apiService → Backend API → JAYRAM Database
 * 
 * Features:
 * - Lists all pages for the current app
 * - Allows selecting a page to edit
 * - Loads page data when selected
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '../../store/useAppStore';
import { useAuthStore } from '../../store/useAuthStore';
import useProfileStore from '../../store/useProfileStore';
import { ProfileIcon, ProfileMenu, ProfileMenuItem } from '../Header/ProfileMenu';
import { FaUserCircle } from 'react-icons/fa';
import { IoSettingsOutline } from 'react-icons/io5';
import { FiLogOut } from 'react-icons/fi';
import Logout from '../Logout/Logut';
import logo from '../../assets/images/newLogo.png';
import { 
  fetchAppPagesWithVersions,  // Fetch all pages from JAYRAM via API
  fetchPageByName              // Fetch specific page data from JAYRAM via API
} from '../../services/firestoreService';
import AppPreviewModal from '../common/AppPreviewModal';

const PageSelector = ({ onPageLoad }) => {
  const navigate = useNavigate();  // For navigation (Dashboard button)
  const { appName } = useAppStore();  // Current app name from global state
  const [pages, setPages] = useState([]);
  const [selectedPage, setSelectedPage] = useState('new');
  const [loading, setLoading] = useState(false);
  const userImage = useProfileStore((state) => state.userImage);

  useEffect(() => {
    loadPages();
  }, [appName]);

  /**
   * Load all pages for the current app from JAYRAM database
   * Called on component mount and when app name changes
   */
  const loadPages = async () => {
    if (!appName) {
      console.warn('⚠️ [PageSelector] No app name available, skipping page load');
      return;
    }
    
    console.log(`📄 [PageSelector] Loading pages for app: ${appName}`);
    setLoading(true);
    try {
      // Fetch pages from JAYRAM via API
      const fetchedPages = await fetchAppPagesWithVersions(appName);
      console.log(`✅ [PageSelector] Loaded ${fetchedPages.length} pages:`, fetchedPages);
      
      // Debug: Log each page's structure
      fetchedPages.forEach((page, index) => {
        console.log(`   Page ${index + 1}:`, {
          id: page.id,
          name: page.name,
          pageName: page.pageName,
          baseName: page.baseName
        });
      });
      
      setPages(fetchedPages);
    } catch (error) {
      console.error('❌ [PageSelector] Failed to load pages:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle page selection from dropdown
   * Fetches selected page data from JAYRAM database and loads it in PageBuilder
   * 
   * IMPORTANT: The dropdown value is the document ID (normalized lowercase)
   * Backend will use this ID directly to fetch the document
   */
  const handlePageChange = async (e) => {
    const selectedValue = e.target.value;
    setSelectedPage(selectedValue);

    // If "Create New Page" is selected
    if (selectedValue === 'new') {
      console.log(`📝 [PageSelector] Creating new page`);
      onPageLoad(null);
      return;
    }

    // Find the selected page to get its display name for logging
    const selectedPageObj = pages.find(p => p.id === selectedValue);
    const displayName = selectedPageObj ? (selectedPageObj.pageName || selectedPageObj.name || selectedPageObj.id) : selectedValue;

    // Load selected page from JAYRAM database
    console.log(`📖 [PageSelector] Loading page - ID: ${selectedValue}, Name: ${displayName}, App: ${appName}`);
    setLoading(true);
    try {
      // CRITICAL: Use document ID for fetching (backend expects this)
      // The backend will use this ID to fetch from Firestore directly
      const pageData = await fetchPageByName(appName, selectedValue);
      
      if (pageData) {
        console.log(`✅ [PageSelector] Page loaded successfully:`, {
          id: pageData.id,
          name: pageData.name,
          pageName: pageData.pageName,
          componentsCount: pageData.components?.length || 0
        });
        onPageLoad(pageData);
      } else {
        console.error(`❌ [PageSelector] No data returned for page ID: ${selectedValue}`);
        alert(`❌ Failed to load page "${displayName}"`);
      }
    } catch (error) {
      console.error('❌ [PageSelector] Failed to load page:', error);
      console.error('Error details:', error.message);
      alert(`❌ Failed to load page "${displayName}". Check console for details.`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      padding: '12px 24px',
      background: '#050618', /* Match Header dark theme */
      borderBottom: '1px solid #e0e0e0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      minHeight: '5.25rem' /* Match header height */
    }}>
      {/* Left Section: MUDUMBAI Logo */}
      <div 
        style={{
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          marginRight: '32px'
        }}
        onClick={() => navigate('/')}
      >
        <img 
          src={logo} 
          alt="MUDUMBAI Logo" 
          style={{
            height: '3.5rem',
            width: 'auto',
            objectFit: 'contain'
          }}
        />
      </div>

      {/* Center Section: App Info and Page Selector */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        flex: 1
      }}>
        {/* App Name */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '8px',
            background: 'rgba(255, 255, 255, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '20px'
          }}>
            🚀
          </div>
          <div>
            <h1 style={{
              margin: 0,
              fontSize: '20px',
              fontWeight: '700',
              color: '#FFFFFF',
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)'
            }}>
              {appName || 'My App'}
            </h1>
            <p style={{
              margin: 0,
              fontSize: '12px',
              color: 'rgba(255, 255, 255, 0.8)'
            }}>
              Page Builder
            </p>
          </div>
          {/* App Preview Button */}
          {appName && (
            <AppPreviewModal appName={appName} />
          )}
        </div>

        {/* Page Selector */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginLeft: '32px'
        }}>
          <label style={{
            fontSize: '14px',
            fontWeight: '600',
            color: '#FFFFFF',
            textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
            minWidth: 'auto'
          }}>
            Current Page:
          </label>
          
          <select
            value={selectedPage}
            onChange={handlePageChange}
            disabled={loading}
            style={{
              minWidth: '200px',
              padding: '10px 14px',
              fontSize: '14px',
              border: '2px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '8px',
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              color: '#374151',
              cursor: loading ? 'wait' : 'pointer',
              fontWeight: '500',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.2s ease'
            }}
            onFocus={(e) => {
              e.target.style.backgroundColor = '#FFFFFF';
              e.target.style.borderColor = '#3B82F6';
              e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
            }}
            onBlur={(e) => {
              e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.95)';
              e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)';
              e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
            }}
          >
            <option value="new">➕ Create New Page</option>
            {pages.length > 0 && <option disabled>───────────────────</option>}
            {pages.map(page => {
              // Show user-friendly page name in dropdown
              // But use document ID for backend fetching
              const documentId = page.id;
              const displayName = page.pageName || page.name || page.id;
              
              return (
                <option key={documentId} value={documentId}>
                  📄 {displayName}
                </option>
              );
            })}
          </select>
        </div>
      </div>

      {/* Right Section: Status & Actions */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
      }}>
          {/* Page Count */}
          <div style={{
            padding: '6px 12px',
            background: 'rgba(255, 255, 255, 0.2)',
            borderRadius: '6px',
            fontSize: '12px',
            color: '#FFFFFF',
            fontWeight: '500'
          }}>
            {pages.length} page{pages.length !== 1 ? 's' : ''}
          </div>

          {/* Loading Indicator */}
          {loading && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '6px 12px',
              background: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '6px'
            }}>
              <div style={{
                width: '14px',
                height: '14px',
                border: '2px solid rgba(255, 255, 255, 0.3)',
                borderTop: '2px solid #FFFFFF',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
              <span style={{ 
                fontSize: '12px', 
                color: '#FFFFFF',
                fontWeight: '500'
              }}>
                Loading...
              </span>
            </div>
          )}

          {/* Dashboard Button - Navigate back to Dashboard */}
          <button
            onClick={() => navigate('/dashboard')}
            style={{
              padding: '8px 14px',
              background: 'rgba(255, 255, 255, 0.2)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '8px',
              color: '#FFFFFF',
              fontSize: '13px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
            onMouseOver={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.3)';
              e.target.style.borderColor = 'rgba(255, 255, 255, 0.5)';
            }}
            onMouseOut={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.2)';
              e.target.style.borderColor = 'rgba(255, 255, 255, 0.3)';
            }}
            title="Go to Dashboard"
          >
            🏠 Dashboard
          </button>

          {/* Refresh Button */}
          <button
            onClick={loadPages}
            disabled={loading}
            style={{
              padding: '8px 14px',
              background: 'rgba(255, 255, 255, 0.2)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '8px',
              color: '#FFFFFF',
              fontSize: '13px',
              fontWeight: '500',
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
            onMouseOver={(e) => {
              if (!loading) {
                e.target.style.background = 'rgba(255, 255, 255, 0.3)';
                e.target.style.borderColor = 'rgba(255, 255, 255, 0.5)';
              }
            }}
            onMouseOut={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.2)';
              e.target.style.borderColor = 'rgba(255, 255, 255, 0.3)';
            }}
            title="Refresh pages list"
          >
            🔄 Refresh
          </button>

          {/* Profile Icon */}
          <div style={{ marginLeft: '12px' }}>
            <ProfileMenu
              trigger={
                <div style={{
                  width: '2.75rem',
                  height: '2.75rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => {
                  e.target.style.borderColor = 'rgba(255, 255, 255, 0.5)';
                }}
                onMouseOut={(e) => {
                  e.target.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                }}
                >
                  <ProfileIcon userImage={userImage} />
                </div>
              }
            >
              <ProfileMenuItem onClick={() => navigate('/userprofile')}>
                <FaUserCircle size={22} /> Profile
              </ProfileMenuItem>
              <ProfileMenuItem>
                <IoSettingsOutline size={22} /> Setting
              </ProfileMenuItem>
              <ProfileMenuItem>
                <FiLogOut size={20} /> <Logout />
              </ProfileMenuItem>
            </ProfileMenu>
          </div>
      </div>

      {/* Add spinning animation */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default PageSelector;
